import React, { useState, useRef, useEffect } from "react";

const COLORS = {
  primary: "#1CA069",
  primaryDark: "#178a5a",
  border: "#dfe8e6",
  text: "#3D4A52",
  muted: "#9aa5a3",
  bgLight: "#F4F9F8",
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function InteractiveCalendar({ label, value, onChange, min, max, presets = [], align = "left" }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const parsedDate = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(parsedDate.getFullYear() || new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(parsedDate.getMonth() ?? new Date().getMonth());

  useEffect(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isDateDisabled = (dateString) => {
    if (min && dateString < min) return true;
    if (max && dateString > max) return true;
    return false;
  };

  const handleSelectDay = (day) => {
    const formattedMonth = String(viewMonth + 1).padStart(2, "0");
    const formattedDay = String(day).padStart(2, "0");
    const dateString = `${viewYear}-${formattedMonth}-${formattedDay}`;

    if (isDateDisabled(dateString)) return;

    onChange(dateString);
    setIsOpen(false);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "Select date";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const minYear = min ? parseInt(min.split("-")[0]) : 2009;
  const maxYear = max ? parseInt(max.split("-")[0]) : new Date().getFullYear();
  const yearsList = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

  return (
    <div ref={containerRef} style={{ display: "flex", flexDirection: "column", minWidth: 165, position: "relative" }}>
      {label && (
        <label
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#7d8a88",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            marginBottom: 6,
            display: "block",
          }}
        >
          {label}
        </label>
      )}

      {/* Button Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px solid ${isOpen ? COLORS.primary : COLORS.border}`,
          borderRadius: 8,
          padding: "9px 12px",
          fontSize: 13.5,
          color: value ? COLORS.text : COLORS.muted,
          background: "#fff",
          width: "100%",
          cursor: "pointer",
          outline: "none",
          boxShadow: isOpen ? "0 0 0 3px rgba(28,160,105,0.15)" : "none",
          transition: "all 0.2s ease",
          fontFamily: "sans-serif",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {formatDateDisplay(value)}
        </span>
        <span
          style={{
            fontSize: 10,
            color: COLORS.muted,
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          ▼
        </span>
      </button>

      {/* Calendar Popover */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            [align === "right" ? "right" : "left"]: 0, // Aligns popover so it doesn't spill off the edge
            marginTop: 6,
            background: "#fff",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            padding: 16,
            boxShadow: "0 12px 30px rgba(7,45,75,0.18)",
            zIndex: 9999, // Ensures calendar floats above all elements
            width: 280,
            boxSizing: "border-box",
            fontFamily: "sans-serif",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button
              type="button"
              onClick={prevMonth}
              style={{ background: "transparent", border: "none", fontSize: 18, color: COLORS.text, cursor: "pointer", padding: "2px 6px" }}
            >
              ‹
            </button>

            <div style={{ display: "flex", gap: 6 }}>
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(parseInt(e.target.value))}
                style={{ border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "3px 6px", fontSize: 12, fontWeight: 600, color: COLORS.text, background: "#fff", outline: "none", cursor: "pointer" }}
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>

              <select
                value={viewYear}
                onChange={(e) => setViewYear(parseInt(e.target.value))}
                style={{ border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "3px 6px", fontSize: 12, fontWeight: 600, color: COLORS.text, background: "#fff", outline: "none", cursor: "pointer" }}
              >
                {yearsList.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={nextMonth}
              style={{ background: "transparent", border: "none", fontSize: 18, color: COLORS.text, cursor: "pointer", padding: "2px 6px" }}
            >
              ›
            </button>
          </div>

          {/* Weekday Labels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 6 }}>
            {DAY_NAMES.map((d) => (
              <span key={d} style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textAlign: "center" }}>
                {d}
              </span>
            ))}
          </div>

          {/* Days Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: presets.length ? 12 : 0 }}>
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={"empty-" + i} />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const formattedMonth = String(viewMonth + 1).padStart(2, "0");
              const formattedDay = String(day).padStart(2, "0");
              const dateString = `${viewYear}-${formattedMonth}-${formattedDay}`;

              const isSelected = value === dateString;
              const disabled = isDateDisabled(dateString);

              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelectDay(day)}
                  style={{
                    height: 28,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 6,
                    border: "none",
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 500,
                    background: isSelected ? COLORS.primary : "transparent",
                    color: disabled ? "#d0d7d5" : isSelected ? "#fff" : COLORS.text,
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !disabled) e.currentTarget.style.background = COLORS.bgLight;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !disabled) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Presets */}
          {presets.length > 0 && (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {presets.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onChange(preset.value);
                    setIsOpen(false);
                  }}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "4px 8px",
                    borderRadius: 6,
                    border: `1px solid ${value === preset.value ? COLORS.primary : COLORS.border}`,
                    background: value === preset.value ? "rgba(28,160,105,0.1)" : "#fff",
                    color: value === preset.value ? COLORS.primaryDark : COLORS.text,
                    cursor: "pointer",
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}